import React, { useState, useEffect, useRef } from 'react';
import { FaCheck, FaTimes, FaPlay, FaPause, FaHeart, FaStar, FaLightbulb, FaTrophy, FaFire, FaCrown, FaUsers, FaClock, FaSpinner } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  joinChallenge, 
  submitChallengeResults, 
  getChallengeParticipants, 
  type ChallengeParticipant 
} from '../../lib/challenges';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import toast from 'react-hot-toast';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: string;
  song_lyrics: string;
}

interface ChallengeFillBlanksExerciseProps {
  song: Song;
  sessionId: string;
  expiresAt: string;
  rewardPoints: number;
  onComplete: (results: any) => void;
  onExit: () => void;
}

interface BlankQuestion {
  id: number;
  sentence: string;
  blanks: { word: string; position: number }[];
  userAnswers: string[];
}

export default function ChallengeFillBlanksExercise({ 
  song, 
  sessionId, 
  expiresAt, 
  rewardPoints,
  onComplete,
  onExit 
}: ChallengeFillBlanksExerciseProps) {
  const [questions, setQuestions] = useState<BlankQuestion[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswers, setUserAnswers] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [startTime] = useState(Date.now());
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef] = useState(new Audio(song.song_url));
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [participants, setParticipants] = useState<ChallengeParticipant[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const [timeBonus, setTimeBonus] = useState(0);
  const [finalScore, setFinalScore] = useState(0);
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    generateQuestions();
    loadParticipants();
    startTimer();
    
    return () => {
      audioRef.pause();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Real-time updates for participants
    const channel = supabase
      .channel(`challenge-${sessionId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'stripeen_challenge_participants',
        filter: `session_id=eq.${sessionId}`
      }, () => {
        loadParticipants();
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [sessionId]);

  const startTimer = () => {
    const updateTimer = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const remaining = Math.max(0, expiry - now);
      
      setTimeLeft(Math.floor(remaining / 1000));
      
      if (remaining <= 0) {
        handleTimeUp();
      }
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);
  };

  const handleTimeUp = () => {
    if (!showResults && !submitting) {
      toast.error('¡Tiempo agotado!');
      calculateResults();
    }
  };

  const loadParticipants = async () => {
    try {
      const participantData = await getChallengeParticipants(sessionId);
      setParticipants(participantData);
    } catch (error) {
      console.error('Error loading participants:', error);
    }
  };

  const handleJoinChallenge = async () => {
    const user = getCurrentUser();
    if (!user) {
      toast.error('Usuario no autenticado');
      return;
    }

    setJoining(true);
    try {
      await joinChallenge(sessionId);
      setHasJoined(true);
      toast.success('¡Te uniste al desafío!');
      loadParticipants();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setJoining(false);
    }
  };

  const generateQuestions = () => {
    const cleanedLyrics = song.song_lyrics
      .replace(/\[.*?\]/g, '')
      .replace(/\(.*?\)/g, '')
      .replace(/\n+/g, '\n')
      .trim();

    const sentences = cleanedLyrics
      .split('\n')
      .map(line => line.trim())
      .filter(line => {
        return line.length > 10 && 
               line.split(' ').length >= 3 &&
               !line.match(/^[A-Z\s]+$/) &&
               line.match(/[a-záéíóúñü]/i);
      });

    // Limit to maximum 50 questions for performance, but allow one per line for most songs
    const maxQuestions = 50;
    const limitedSentences = sentences.slice(0, maxQuestions);
    
    const generatedQuestions: BlankQuestion[] = limitedSentences.map((sentence, index) => {
      const words = sentence.split(' ');

      const meaningfulWords = words
        .map((word, idx) => ({ word: word.replace(/[.,!?;:"()]/g, ''), index: idx, length: word.length }))
        .filter(item => {
          const cleanWord = item.word.toLowerCase();
          const skipWords = ['el', 'la', 'los', 'las', 'un', 'una', 'de', 'del', 'en', 'con', 'por', 'para', 'que', 'se', 'te', 'me', 'le', 'lo', 'su', 'mi', 'tu', 'es', 'son', 'y', 'o', 'a'];
          return item.length > 3 && !skipWords.includes(cleanWord);
        })
        ; // Don't limit here, we'll handle it below

      const numBlanks = Math.min(2, Math.max(1, Math.floor(meaningfulWords.length / 4))); // 1-2 blanks per question for challenge mode
      const selectedWords = meaningfulWords.slice(0, numBlanks);

      const blanks = selectedWords.map(item => ({
        word: item.word,
        position: item.index
      }));

      let sentenceWithBlanks = sentence;
      const blankPositions = selectedWords.map(w => w.index).sort((a, b) => b - a);
      
      blankPositions.forEach(pos => {
        const wordsArray = sentenceWithBlanks.split(' ');
        if (pos < wordsArray.length) {
          wordsArray[pos] = '_____';
          sentenceWithBlanks = wordsArray.join(' ');
        }
      });

      return {
        id: index,
        sentence: sentenceWithBlanks,
        blanks,
        userAnswers: new Array(blanks.length).fill('')
      };
    });

    console.log(`Generated ${generatedQuestions.length} questions from ${sentences.length} lyric lines (max: ${maxQuestions})`);
    
    setQuestions(generatedQuestions);
    if (generatedQuestions.length > 0) {
      setUserAnswers(new Array(generatedQuestions[0].blanks.length).fill(''));
    }
  };

  const handleAnswerChange = (blankIndex: number, value: string) => {
    const newAnswers = [...userAnswers];
    newAnswers[blankIndex] = value;
    setUserAnswers(newAnswers);
  };

  const handleNextQuestion = () => {
    const updatedQuestions = [...questions];
    updatedQuestions[currentQuestion].userAnswers = [...userAnswers];
    setQuestions(updatedQuestions);

    const question = questions[currentQuestion];
    let correctCount = 0;
    question.blanks.forEach((blank, index) => {
      const userAnswer = userAnswers[index]?.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
      const correctAnswer = blank.word.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
      
      if (userAnswer === correctAnswer || 
          userAnswer.includes(correctAnswer) || 
          correctAnswer.includes(userAnswer)) {
        correctCount++;
      }
    });

    if (correctCount === question.blanks.length) {
      setStreak(prev => prev + 1);
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setUserAnswers(new Array(questions[currentQuestion + 1].blanks.length).fill(''));
    } else {
      calculateResults();
    }
  };

  const calculateTimeBonus = (completionTimeSeconds: number): number => {
    const completionMinutes = Math.floor(completionTimeSeconds / 60);
    const questionCount = questions.length;
    
    // Adjust time thresholds based on number of questions
    // For longer challenges, give more time for bonus points
    if (questionCount <= 10) {
      // Short challenges (≤10 questions): original timing
      if (completionMinutes <= 1) return 15;
      if (completionMinutes <= 2) return 10;
      if (completionMinutes <= 3) return 5;
    } else if (questionCount <= 20) {
      // Medium challenges (11-20 questions): extended timing
      if (completionMinutes <= 2) return 15;
      if (completionMinutes <= 4) return 10;
      if (completionMinutes <= 6) return 5;
    } else {
      // Long challenges (21+ questions): generous timing
      if (completionMinutes <= 3) return 15;
      if (completionMinutes <= 6) return 10;
      if (completionMinutes <= 9) return 5;
    }
    
    return 0; // No bonus for slow completion
  };

  const calculateResults = async () => {
    let totalCorrect = 0;
    let totalQuestions = 0;

    questions.forEach(question => {
      question.blanks.forEach((blank, index) => {
        totalQuestions++;
        const userAnswer = question.userAnswers[index]?.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
        const correctAnswer = blank.word.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
        
        if (userAnswer === correctAnswer || 
            userAnswer.includes(correctAnswer) || 
            correctAnswer.includes(userAnswer)) {
          totalCorrect++;
        }
      });
    });

    setScore(totalCorrect);
    
    const completionTime = Math.floor((Date.now() - startTime) / 1000);
    const bonus = calculateTimeBonus(completionTime);
    const finalScoreWithBonus = totalCorrect + bonus;
    
    setTimeBonus(bonus);
    setFinalScore(finalScoreWithBonus);
    setShowResults(true);

    // Submit to challenge system
    setSubmitting(true);
    try {
      await submitChallengeResults(sessionId, finalScoreWithBonus, totalQuestions, completionTime);
      loadParticipants(); // Refresh leaderboard
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }

    onComplete({
      score: finalScoreWithBonus,
      totalQuestions,
      timeTaken: completionTime,
      answers: questions.map(q => q.userAnswers),
      isChallenge: true,
      sessionId,
      baseScore: totalCorrect,
      timeBonus: bonus
    });
  };

  const toggleAudio = () => {
    if (isPlaying) {
      audioRef.pause();
    } else {
      audioRef.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    
    if (hours > 0) {
      return `${hours}h ${remainingMins}m`;
    }
    return `${mins}m`;
  };

  const renderJoinScreen = () => (
    <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
      <div className="space-y-4 sm:space-y-6">
        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto animate-pulse">
          <FaTrophy className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
        </div>
        
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">🏆 Modo Desafío</h2>
          <p className="text-white/80 text-base sm:text-lg">
            Compite con otros estudiantes en tiempo real
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-yellow-400/30">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">{rewardPoints}</div>
              <div className="text-sm text-white/70">Puntos Premio</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{formatTime(timeLeft)}</div>
              <div className="text-sm text-white/70">Tiempo Restante</div>
            </div>
          </div>
          
          <div className="text-center mb-4">
            <div className="text-lg font-bold text-white">{participants.length}</div>
            <div className="text-sm text-white/70">Participantes Actuales</div>
          </div>
          
          <div className="text-center mb-4">
            <div className="text-lg font-bold text-blue-400">{questions.length}</div>
            <div className="text-sm text-white/70">Preguntas del Desafío</div>
          </div>
          
          <div className="text-center mb-4">
            <div className="text-xs text-white/60 bg-white/5 rounded-lg p-2">
              ⚡ Bonus de tiempo ajustado según la cantidad de preguntas
            </div>
          </div>

          <button
            onClick={handleJoinChallenge}
            disabled={joining || timeLeft <= 0}
            className="w-full px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-xl hover:from-yellow-500 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg font-bold"
          >
            {joining ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Uniéndose...
              </>
            ) : (
              <>
                <FaUsers className="mr-2" />
                Unirse al Desafío
              </>
            )}
          </button>
        </div>

        <button
          onClick={onExit}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Volver a Práctica Normal
        </button>
      </div>
    </div>
  );

  const renderQuestion = () => {
    const question = questions[currentQuestion];
    if (!question) return null;

    const progress = ((currentQuestion + 1) / questions.length) * 100;

    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        {/* Challenge Header with Timer */}
        <div className="bg-black/90 backdrop-blur-lg rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-gray-700/50">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <FaTrophy className="text-yellow-400 w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-white font-bold text-xs sm:text-sm">DESAFÍO</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <FaClock className="text-red-400 w-3 h-3 sm:w-4 sm:h-4" />
                <span className={`font-bold text-xs sm:text-sm ${timeLeft <= 60 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
                  {timeLeft <= 60 ? `${timeLeft}s` : formatTime(timeLeft)}
                </span>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <FaUsers className="text-blue-400 w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-white font-bold text-xs sm:text-sm">{participants.length}</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
            <div>
              <div className="text-sm sm:text-lg font-bold text-yellow-400">{rewardPoints}</div>
              <div className="text-xs text-white/70">Pts</div>
            </div>
            <div>
              <div className="text-sm sm:text-lg font-bold text-white">{currentQuestion + 1}/{questions.length}</div>
              <div className="text-xs text-white/70">Preg</div>
            </div>
            <div>
              <div className="text-sm sm:text-lg font-bold text-red-400">{streak}</div>
              <div className="text-xs text-white/70">Racha</div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative w-full h-3 bg-white/20 rounded-full overflow-hidden">
          <motion.div 
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          >
            <div className="absolute inset-0 bg-white/30 animate-pulse rounded-full"></div>
          </motion.div>
        </div>

        {/* Main Question Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-yellow-400/30 shadow-2xl">
          <div className="text-center mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4">
              Completa las palabras que faltan
            </h3>
            <div className="text-base sm:text-lg text-white/80 leading-relaxed">
              {question.sentence.split('_____').map((part, index) => (
                <span key={index} className="inline-block">
                  {part}
                  {index < question.blanks.length && (
                    <input
                      type="text"
                      value={userAnswers[index] || ''}
                      onChange={(e) => handleAnswerChange(index, e.target.value)}
                      className="mx-1 sm:mx-2 px-2 sm:px-3 py-1 sm:py-2 bg-black/30 backdrop-blur-lg border border-yellow-400/50 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-center font-bold transition-all duration-300 hover:bg-black/40 text-sm sm:text-base"
                      placeholder="..."
                      style={{ 
                        width: `${Math.max(80, Math.min(150, (question.blanks[index]?.word.length || 5) * 15))}px`,
                        maxWidth: '90vw'
                      }}
                    />
                  )}
                </span>
              ))}
            </div>
          </div>

          <div className="text-center mb-4">
            <div className="p-3 bg-blue-400/10 backdrop-blur-lg rounded-xl border border-blue-400/20">
              <p className="text-blue-200 text-xs sm:text-sm">
                ⚡ ¡Responde rápido para ganar más puntos!
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            onClick={toggleAudio}
            className="w-full sm:w-auto flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-400 to-purple-500 text-white rounded-xl hover:from-blue-500 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
          >
            {isPlaying ? <FaPause className="mr-2" /> : <FaPlay className="mr-2" />}
            {isPlaying ? 'Pausar' : 'Escuchar'}
          </button>

          <button
            onClick={handleNextQuestion}
            disabled={userAnswers.some(answer => !answer.trim()) || timeLeft <= 0}
            className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-xl hover:from-yellow-500 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
          >
            {currentQuestion < questions.length - 1 ? 'Siguiente' : 'Terminar'}
            <FaCheck className="ml-2" />
          </button>
        </div>

        {/* Live Participants */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-4 border border-white/10">
          <h4 className="text-white font-semibold mb-3 flex items-center">
            <FaUsers className="mr-2 text-blue-400" />
            Participantes en Vivo ({participants.length})
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {participants.slice(0, 6).map((participant, index) => (
              <div key={participant.id} className="flex items-center space-x-2 p-2 bg-white/5 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-white">
                  {participant.user?.email?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white/80 truncate">
                    {participant.user?.full_name || participant.user?.email || 'Usuario'}
                  </div>
                  {participant.completed_at && (
                    <div className="text-xs text-green-400">✓ Completado</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const totalQuestions = questions.reduce((sum, q) => sum + q.blanks.length, 0);
    const basePercentage = Math.round((score / totalQuestions) * 100);
    const completionTime = Math.floor((Date.now() - startTime) / 1000);
    
    // Calculate user's rank in this challenge
    const completedParticipants = participants.filter(p => p.completed_at);
    const userParticipant = participants.find(p => p.user_id === getCurrentUser()?.id);
    const userRank = completedParticipants
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return (a.completion_time || 999) - (b.completion_time || 999);
      })
      .findIndex(p => p.user_id === getCurrentUser()?.id) + 1;

    return (
      <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
        {/* Challenge Results Header */}
        <div className="space-y-4">
          <div className="text-6xl animate-bounce">
            {userRank === 1 ? '👑' : userRank <= 3 ? '🏆' : '🎯'}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            {userRank === 1 ? '¡Campeón!' : userRank <= 3 ? '¡Podio!' : '¡Bien Hecho!'}
          </h2>
          <div className="space-y-2">
            <div className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent animate-pulse">
              {finalScore} Puntos
            </div>
            <div className="text-lg sm:text-xl text-white/80">
              {score} correctas + {timeBonus} bonus tiempo
            </div>
          </div>
          <p className="text-white/80 text-lg">
            Posición #{userRank} de {completedParticipants.length} participantes
          </p>
        </div>

        {/* Challenge Stats */}
        <div className="bg-gradient-to-r from-green-400/20 to-blue-400/20 backdrop-blur-lg rounded-xl p-4 border border-green-400/30 mb-4">
          <h4 className="text-white font-semibold mb-3 text-center">💰 Sistema de Bonus por Tiempo</h4>
          <div className="grid grid-cols-2 gap-3 text-center">
            {(() => {
              const questionCount = questions.length;
              if (questionCount <= 10) {
                return (
                  <>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-green-400">≤1 min</div>
                      <div className="text-xs text-white/70">+15 pts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-yellow-400">≤2 min</div>
                      <div className="text-xs text-white/70">+10 pts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-orange-400">≤3 min</div>
                      <div className="text-xs text-white/70">+5 pts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-red-400">{'>'}3 min</div>
                      <div className="text-xs text-white/70">+0 pts</div>
                    </div>
                  </>
                );
              } else if (questionCount <= 20) {
                return (
                  <>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-green-400">≤2 min</div>
                      <div className="text-xs text-white/70">+15 pts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-yellow-400">≤4 min</div>
                      <div className="text-xs text-white/70">+10 pts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-orange-400">≤6 min</div>
                      <div className="text-xs text-white/70">+5 pts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-red-400">{'>'}6 min</div>
                      <div className="text-xs text-white/70">+0 pts</div>
                    </div>
                  </>
                );
              } else {
                return (
                  <>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-green-400">≤3 min</div>
                      <div className="text-xs text-white/70">+15 pts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-yellow-400">≤6 min</div>
                      <div className="text-xs text-white/70">+10 pts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-orange-400">≤9 min</div>
                      <div className="text-xs text-white/70">+5 pts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-sm font-bold text-red-400">{'>'}9 min</div>
                      <div className="text-xs text-white/70">+0 pts</div>
                    </div>
                  </>
                );
              }
            })()}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-white/10 rounded-xl p-3 border border-white/20">
            <div className="text-lg font-bold text-yellow-400">{finalScore}</div>
            <div className="text-xs text-white/70">Puntos Finales</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 border border-white/20">
            <div className="text-lg font-bold text-white">{score}/{totalQuestions}</div>
            <div className="text-xs text-white/70">Correctas</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 border border-white/20">
            <div className="text-lg font-bold text-green-400">{timeBonus}</div>
            <div className="text-xs text-white/70">Bonus Tiempo</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 border border-white/20">
            <div className="text-lg font-bold text-blue-400">{formatTime(completionTime)}</div>
            <div className="text-xs text-white/70">Tiempo</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 border border-white/20">
            <div className="text-lg font-bold text-red-400">{streak}</div>
            <div className="text-xs text-white/70">Racha</div>
          </div>
        </div>

        {/* Challenge Leaderboard */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-white/20">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center justify-center">
            <FaTrophy className="mr-2 text-yellow-400" />
            Clasificación del Desafío
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {completedParticipants
              .sort((a, b) => {
                if (b.score !== a.score) return b.score - a.score;
                return (a.completion_time || 999) - (b.completion_time || 999);
              })
              .slice(0, 10)
              .map((participant, index) => (
                <div 
                  key={participant.id} 
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    index === 0 ? 'bg-yellow-400/20 border border-yellow-400/50' :
                    index === 1 ? 'bg-gray-300/20 border border-gray-300/50' :
                    index === 2 ? 'bg-orange-400/20 border border-orange-400/50' :
                    'bg-white/5'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-400 text-black' :
                      index === 1 ? 'bg-gray-300 text-black' :
                      index === 2 ? 'bg-orange-400 text-black' :
                      'bg-gray-600 text-white'
                    }`}>
                      {index === 0 ? '👑' : index + 1}
                    </div>
                    <div className="text-left">
                      <div className="text-white font-medium text-sm">
                        {participant.user?.full_name || participant.user?.email || 'Usuario'}
                      </div>
                      <div className="text-xs text-white/60">
                        {participant.completion_time ? formatTime(participant.completion_time) : 'En progreso...'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold">{participant.score}</div>
                    <div className="text-xs text-yellow-400">+{participant.points_earned} pts</div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={() => window.location.reload()}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-xl hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold"
          >
            Nuevo Desafío
          </button>
          <button
            onClick={onExit}
            className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all duration-300 font-bold"
          >
            Salir
          </button>
        </div>
      </div>
    );
  };

  if (!hasJoined) {
    return renderJoinScreen();
  }

  if (questions.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-yellow-400 border-t-transparent mx-auto mb-4"></div>
        <p className="text-white text-base sm:text-lg">Preparando desafío...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[600px] relative">
      {showResults ? renderResults() : renderQuestion()}
      
      {/* Floating Exit Button */}
      <button
        onClick={onExit}
        className="absolute top-4 right-4 p-2 bg-red-500/20 hover:bg-red-500/30 rounded-full text-red-400 hover:text-red-300 transition-colors border border-red-500/30"
      >
        <FaTimes className="w-4 h-4" />
      </button>
    </div>
  );
}